(ns ui.fields
  (:require
   ["@radix-ui/react-checkbox" :as RadixCheckbox]
   ["@radix-ui/react-radio-group" :as RadixRadio]
   ["@radix-ui/react-scroll-area" :as RadixScrollArea]
   ["@radix-ui/react-select" :as RadixSelect]
   ["@radix-ui/react-switch" :as RadixSwitch]
   ["lucide-react" :refer [Check ChevronDown HelpCircle Minus Search]]
   ["react" :as r]
   ["virtua" :refer [Virtualizer]]
   [applied-science.js-interop :as jsi]
   [clojure.string :as str]
   [minorodata.app.utils :as utils]
   [ui.chip]
   [ui.dropdown-menu]
   [ui.tooltip :as tooltip]
   [ui.typography :as typography :refer [label]]
   [ui.utils :refer [clsx separate-props]]
   [ui.popover :as popover]))


(def container-context (r/createContext nil))

(def reset-value "__none__")
(def empty-value "__empty__")

(defn input-class [{:keys [errors id disabled class input-props icon icon-right size]}]
  (clsx "block w-full text-sm rounded-md border-0 ring-1 outline-0 bg-white ring-inset ring-kleene-neutral-01 placeholder:text-kleene-dark-05 focus:ring-2! focus:ring-inset focus:ring-kleene-secondary-03"
        "dark:bg-kleene-dark-01 dark:text-kleene-neutral-01 dark:ring-kleene-dark-03 dark:focus:ring-kleene-dark-04"
        (if (= size :large) "px-6 py-3" "px-3 py-2")
        {"ring-kleene-error-02 focus:ring-kleene-error-01" (get errors id)
         "disabled:bg-kleene-neutral-05 disabled:text-kleene-dark-05 dark:disabled:text-kleene-dark-05 dark:disabled:bg-kleene-dark-01 disabled:cursor-not-allowed " disabled
         (if (= size :large) "pl-12" "pl-10") icon
         (if (= size :large) "pr-12" "pr-10") icon-right}
        (:class input-props)
        class))

(defn input [{:keys [id name label auto-focus textarea required placeholder helper label-helper disabled field errors size default-value input-ref
                     full-width class data-cy rows input-props icon icon-right value on-change on-focus on-blur on-click type controlled
                     readonly]
              :or   {required    false
                     controlled  true
                     full-width  true
                     input-props {}}
              :as   props}]
  (let [error     (get errors id)
        value     (or (:value field) value)
        size      (or size :regular)
        node      (if textarea :textarea :input)
        icon-size (if (= :large size) 24 20)
        action    (or (:action field) on-change)]
    [:div {:class   (clsx "min-w-[268px]" {"w-full" full-width} class)
           :data-cy (when data-cy
                      (str data-cy "-wrapper"))}
     (when label
       [typography/label {:for     (or id name)
                          :htmlFor (or id name)}
        label
        (when required " *")
        (when label-helper
          [tooltip/tooltip {:content label-helper}
           [:> HelpCircle {:size 16
                           :class "inline-block -mt-px ml-1 text-kleene-text-secondary"}]])])
     [:div {:class "w-full"}
      [:div {:class "relative"}
       (when icon
         [:div {:class "absolute top-1/2 left-3 -translate-y-1/2 pointer-events-none"}
          [:> icon {:size icon-size
                    :class "block text-kleene-dark-05"}]])
       (when icon-right
         [:div {:class "absolute top-1/2 right-3 -translate-y-1/2 pointer-events-none"}
          [:> icon-right {:size icon-size
                          :class "block text-kleene-dark-05"}]])
       [node (-> input-props
                 (utils/assoc-some
                  :required      required
                  :data-cy       data-cy
                  :placeholder   placeholder
                  :auto-focus    auto-focus
                  :id            id
                  :name          name
                  :on-change     (when action (fn [evt] (action evt)))
                  :on-focus      on-focus
                  :on-blur       on-blur
                  :value         (when controlled value)
                  :disabled      disabled
                  :default-value (or default-value value)
                  :on-click      on-click
                  :ref           input-ref
                  :type          type
                  :rows          rows
                  :readonly      readonly
                  :class         (input-class props)))]]
      (when (or error helper)
        [:div {:class "mt-1 text-xs leading-5"}
         (cond
           error [:span {:class "text-kleene-error-01"} (:value error)]
           helper [:span {:class "text-kleene-dark-05 dark:text-kleene-neutral-01"} helper])])]]))


(defn select-trigger-class
  [{:keys [icon size error class disabled variant]}]
  (clsx "relative text-left w-full cursor-pointer text-sm rounded-md border-0 ring-1 pl-3 pr-8 outline-0 bg-white ring-inset ring-kleene-neutral-01 data-placeholder:text-kleene-dark-05 focus:ring-2 focus:ring-inset focus:ring-kleene-secondary-03 radix-state-open:ring-kleene-secondary-03 radix-state-open:ring-2 whitespace-nowrap overflow-hidden text-ellipsis min-h-9 flex items-center"
        "dark:bg-kleene-dark-01 dark:text-kleene-neutral-01 dark:ring-kleene-dark-03 dark:radix-state-open:ring-kleene-dark-04"

        {(clsx "text-kleene-dark-01 bg-kleene-neutral-05 hover:bg-kleene-neutral-04 focus:ring-kleene-neutral-04 ring-0"
               "dark:bg-kleene-dark-02 dark:hover:bg-kleene-dark-03 dark:text-kleene-neutral-01") (= :empty variant)
         (clsx "border-0! ring-0! p-0! pr-8! bg-inherit! text-inherit!") (= :text variant)
         (clsx "text-xs") (= :small size)
         "ring-kleene-error-02 focus:ring-kleene-error-01" error
         "disabled:bg-kleene-neutral-05 disabled:text-kleene-dark-05 disabled:cursor-not-allowed dark:disabled:text-kleene-dark-05/50 dark:disabled:bg-kleene-dark-01" disabled
         (if (= size :large) "pl-12" "pl-10") icon}
        (if (= size :large) "py-3" "py-2")
        class))

(defn select-content-class [{:keys [content-class]}]
  content-class)

(defn select-native [{:keys [id value on-change auto-focus required options disabled placeholder data-cy]
                      :as   props
                      :or   {auto-focus false
                             required   false
                             disabled   false
                             options    []}}]
  [:select {:id          id
            :name        id
            :value       value
            :data-cy     data-cy
            :disabled    disabled
            :on-change   on-change
            :autoFocus   auto-focus
            :placeholder placeholder
            :class       (select-trigger-class props)
            :required    required}
   (for [{:keys [value text]} options]
     ^{:key value}
     [:option {:value value} (or text value)])])

(defn option-item [{:keys [value text data-cy visible]}]
  [:> RadixSelect/Item {:value (if (= "" value) empty-value value)
                        :key   value
                        :title text
                        :class (clsx "relative px-2 py-1.5 outline-hidden text-sm hover:text-kleene-dark-05 transition-colors cursor-pointer text-ellipsis overflow-hidden"
                                     {"hidden" (false? visible)})
                        :data-cy (when data-cy (str data-cy "-" value))}
   [:> RadixSelect/ItemText {:class "relative z-10"} (or text value)]
   [:> RadixSelect/ItemIndicator {:class "absolute inset-0 bg-kleene-selected-background text-kleene-text px-2 py-1.5 opacity-50"}
    text]])

(defn reset-option? [{:keys [value]}]
  (or (= reset-value value)
      (and (or (nil? value) (string? value))
           (empty? value))))

(defn render-options
  [{:keys [options]}]
  (let [first-option (first options)
        has-reset? (and (not (= :group (:type first-option))) ;; ignore reset option logic if there are groups
                        (reset-option? first-option))
        [reset options] (if has-reset?
                          [(first options) (rest options)]
                          [nil options])]
    [:<>
     (when reset [option-item (assoc reset :value reset-value)])
     (for [o options]
       (case (:type o)
         :group
         [:> RadixSelect/Group {:key (:name o)}
          [:> RadixSelect/Label {:class "p-2 font-medium"} (:name o)]
          [:f> render-options o]]

         :separator
         [:> RadixSelect/Separator {:key (:key o) :class "h-px bg-kleene-layout-border my-1"}]

         ^{:key (or (:value o) (:text o))}
         [option-item o]))]))

(defn render-virtualized-options [{:keys [value options scroll-ref]}]
  [:> Virtualizer {:scroll-ref  scroll-ref
                   :overscan    2
                   :keep-mounted (keep-indexed (fn [idx itm]
                                                 (when (= value (:value itm)) idx))
                                               options)}
   (for [{:keys [value text data-cy]} options]
     [:> RadixSelect/Item {:value value
                           :key   value
                           :class (clsx "relative px-2 py-1.5 outline-hidden text-sm hover:text-kleene-dark-05 transition-colors cursor-pointer text-ellipsis")
                           :data-cy (when data-cy (str data-cy "-" value))}
      [:> RadixSelect/ItemText {:class "relative z-10"} (or text value)]
      [:> RadixSelect/ItemIndicator {:class "absolute inset-0 bg-kleene-selected-background text-kleene-text px-2 py-1.5 opacity-50"}
       text]])])

(defn select-radix-content' [{:keys [value options searchable]}]
  (let [[search set-search] (r/useState "")
        scroll-ref          (r/useRef nil)

        virtualized         (> (count options) 1000)
        show-search         (and searchable (> (count options) 10))
        filtered-options    (cond->> options
                              (and searchable (not-empty search))
                              (filter (fn [{:keys [text] :as opt}]
                                        (or (str/includes? (str/lower-case text) (str/lower-case search))
                                            (str/includes? (str/lower-case value) (str/lower-case search))
                                            (= (str/lower-case value) (str/lower-case (:value opt)))))))]
    [:<>
     (when show-search
       [:div {:class "p-2 border-b" :on-key-down (fn [e] (.stopPropagation e))}
        [input {:placeholder "Search options"
                :icon        Search
                :auto-focus  true
                :value       search
                :on-change   (fn [e]
                               (set-search (-> e .-target .-value)))}]])
     [:> RadixScrollArea/Root
      [:> RadixSelect/Viewport {:asChild true}
       [:> RadixScrollArea/Viewport {:class "h-full relative"
                                     :style {:max-height (str "min(var(--radix-select-content-available-height), " (if show-search 348 400) "px)")
                                             :overflowY  js/undefined}
                                     :ref    scroll-ref}

        (if virtualized
          [:f> render-virtualized-options {:value      value
                                           :options    filtered-options
                                           :scroll-ref scroll-ref}]
          [:f> render-options {:options filtered-options}])]]
      [:> RadixScrollArea/Scrollbar {:orientation "vertical" :class "w-3 px-0.5 py-1 z-20" :style {:box-sizing "unset"}}
       [:> RadixScrollArea/Thumb {:class "w-full bg-kleene-neutral-02 dark:bg-kleene-dark-03 rounded-md"}]]]]))

(defn select-radix-content
  [{:keys [class] :as props}]
  (let [container (r/useContext container-context)]
    [:<>
     [:> RadixSelect/Portal {:container (jsi/get container :current)}
      [:> RadixSelect/Content {:position   "popper"
                               :sideOffset 10
                               :class      (clsx "z-2500 items-stretch bg-white rounded-md p-px text-base shadow-lg ring-inset ring-1 overflow-hidden ring-kleene-neutral-05 focus:outline-hidden dark:bg-kleene-dark-02 dark:ring-kleene-dark-03 pointer-events-auto!"
                                                 class)
                               :style      {:min-width  "var(--radix-select-trigger-width)"
                                            :max-height "min(var(--radix-select-content-available-height), 400px)"
                                            ;; HACK: assume that with container dropdown can overflow and be cropped
                                            :max-width  (when container "var(--radix-popper-anchor-width)")}}
       [:f> select-radix-content' props]]]]))

(defn select-root [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixSelect/Root props]
          children)))

(defn select-trigger [& args]
  (let [[props children] (separate-props args)]
    (into [:> RadixSelect/Trigger props]
          children)))

(defn select-radix [{:keys [id value on-change required searchable options disabled placeholder data-cy]
                     :as   props
                     :or   {required   false
                            disabled   false
                            options    []}}]
  [select-root {:value         value
                :onValueChange #(on-change (condp = %
                                             reset-value nil
                                             empty-value ""
                                             %))
                :disabled      disabled
                :required      required}
   [select-trigger {:id      id
                    :data-cy data-cy
                    :class   (select-trigger-class props)}
    [:div {:class "overflow-hidden text-ellipsis"}
     [:> RadixSelect/Value {:placeholder placeholder}]]
    [:> RadixSelect/Icon {:as-child true
                          :class    "absolute right-2"}
     [:> ChevronDown {:size 20}]]]

   [:f> select-radix-content {:value       value
                              :options     options
                              :searchable  searchable
                              :class       (select-content-class props)}]])

(defn select-like [{:keys [value field error id label required helper full-width size icon class disabled
                           data-cy placeholder label-props key]
                    :as   props
                    :or   {required   false
                           full-width true}}
                   children]
  (let [value (or value (:value field))
        icon-size (if (= :large size) 24 20)]
    [:div {:class   (clsx {"min-w-[150px]" (not full-width)
                           "w-full"        full-width}
                          class)
           :key     key
           :data-cy (when data-cy (str data-cy "-wrapper"))}
     (when label
       [typography/label (merge {:for     id
                                 :htmlFor id}
                                label-props)
        label (when required " *")])

     [:div
      [:div {:class "relative"}
       (when icon
         [:div {:class "absolute top-1/2 left-3 -translate-y-1/2 pointer-events-none"}
          [:> icon {:size  icon-size
                    :class "block text-kleene-dark-05"}]])

       [ui.dropdown-menu/dropdown-menu-root
        [ui.dropdown-menu/dropdown-menu-trigger {:asChild  true
                                                 :disabled disabled}
         [:div {:class   (clsx (select-trigger-class props)
                               {"ring-kleene-error-02 focus:ring-kleene-error-01" error
                                "disabled:bg-kleene-neutral-05 disabled:text-kleene-dark-05 dark:disabled:text-kleene-dark-05 dark:disabled:bg-kleene-dark-01 disabled:cursor-not-allowed " disabled})
                :data-cy data-cy}

          (if (empty? value)
            [:span {:class "text-kleene-text-secondary"} placeholder]
            value)

          [:> RadixSelect/Icon {:as-child true
                                :class    "absolute right-2"}
           [:> ChevronDown {:size 20}]]]]

        [ui.dropdown-menu/dropdown-menu-content {:hide-arrow true
                                                 :sideOffset 10
                                                 :align "start"
                                                 :class (clsx "z-2500 items-stretch bg-white rounded-md p-px text-base shadow-lg ring-inset ring-1 overflow-hidden ring-kleene-neutral-05 focus:outline-hidden w-full"
                                                              "dark:bg-kleene-dark-02 dark:ring-kleene-dark-03")
                                                 :style {:min-width "var(--radix-dropdown-menu-trigger-width)"
                                                         :max-height  "min(var(--radix-dropdown-menu-content-available-height), 400px)"}}
         children]]]
      (when (or error helper)
        [:div {:class "mt-1 text-xs leading-5"}
         (cond
           error [:span {:class "text-kleene-error-01"} (or (:value error)
                                                            error)]
           helper [:span {:class "text-kleene-dark-05"} helper])])]]))

(defn searchable-select' [{:keys [value options on-change] :as props}]
  (let [list-ref (r/useRef nil)
        [search set-search] (r/useState "")
        filtered-options    (cond->> options
                              (not-empty search)
                              (filter (fn [{:keys [text] :as opt}]
                                        (or (str/includes? (str/lower-case text) (str/lower-case search))
                                            (str/includes? (str/lower-case (or value "")) (str/lower-case search))
                                            (= (str/lower-case (or value "") ) (str/lower-case (:value opt)))))))]
    (r/useEffect
     (fn []
       (when-let [list-el (.-current list-ref)]
         (when-let [selected-el (.querySelector list-el "[data-selected]")]
           (js/setTimeout #(.scrollIntoView selected-el #js {:block "nearest"}) 100)))
       js/undefined)
     #js [])

    [:<>
     [:div {:class "p-2 border-b"}
      [input {:placeholder "Search options"
              :icon        Search
              :auto-focus  true
              :value       search
              :on-change   (fn [e]
                             (set-search (-> e .-target .-value)))}]]
     [:> RadixScrollArea/Root {:data-slot "scroll-area"}
      [:> RadixScrollArea/Viewport {:as-child true
                                    :ref  list-ref
                                    :style {:max-height "calc(min(var(--radix-popper-available-height), 400px) - 54px)"}}
       [:div {:role "listbox"}
        (for [{:keys [value text data-cy]} filtered-options]
          [popover/popover-close {:key value :as-child true}
           [:div {:role "option"
                  :on-click #(on-change value)
                  :data-selected (or (= value (:value props)) nil)
                  :class "relative px-2 py-1.5 outline-hidden text-sm hover:text-kleene-dark-05 transition-colors cursor-pointer text-ellipsis data-selected:bg-kleene-selected-background"
                  :data-cy (when data-cy (str data-cy "-" value))}
            text]])]]
      [:> RadixScrollArea/Scrollbar {:orientation "vertical" :class "w-3 px-0.5 py-1 z-20" :style {:box-sizing "unset"}}
       [:> RadixScrollArea/Thumb {:class "w-full bg-kleene-neutral-02 dark:bg-kleene-dark-03 rounded-md"}]]]]))

(defn searchable-select [{:keys [field errors id label label-props required options full-width class disabled on-change placeholder data-cy]
                          :as   props
                          :or   {required false
                                 options  []
                                 full-width true}}]
  (let [value (or (:value field) "")
        error (get errors id)
        on-change (or on-change
                      (fn [evt]
                        ((:action field) evt)
                        false))]
    [:div {:class   (clsx {"min-w-[150px]" (not full-width)
                           "w-full"        full-width}
                          class)
           :data-cy (when data-cy (str data-cy "-wrapper"))}
     (when label
       [typography/label (merge {:for     id
                                 :htmlFor id}
                                label-props)
        label (when required " *")])
     [:div
      [popover/popover-root {:modal true}
       [popover/popover-trigger {:as-child true}
        [:div {:data-disabled disabled
               :tab-index -1
               :class   (clsx (select-trigger-class props)
                              {"ring-kleene-error-02 focus:ring-kleene-error-01" error
                               "disabled:bg-kleene-neutral-05 disabled:text-kleene-dark-05 dark:disabled:text-kleene-dark-05 dark:disabled:bg-kleene-dark-01 disabled:cursor-not-allowed pointer-events-none" disabled})
               :data-cy data-cy
               :id      id}

         (if (empty? value)
           [:span {:class "text-kleene-text-secondary"} placeholder]
           value)

         [:> RadixSelect/Icon {:as-child true
                               :class    "absolute right-2"}
          [:> ChevronDown {:size 20}]]]]
       [popover/popover-content {:class "pointer-events-auto overflow-hidden p-0"
                                 :style {:min-width  "var(--radix-popper-anchor-width)"
                                         :max-height "min(var(--radix-popper-available-height), 400px)"
                                         :max-width  "var(--radix-popper-anchor-width)"}}
        [:f> searchable-select' {:value value
                                 :options options
                                 :on-change on-change}]]]]]))

(defn multiselect [{:keys [field errors id value on-change options]
                    :as props
                    :or   {field      {}
                           options    []}}]
  (let [error       (get errors id)
        field-value (or value
                        (:value field)
                        [])
        on-change (or on-change
                      (fn [evt]
                        ((:action field) evt)
                        false))]
    [select-like
     (assoc props
            :value (into [:div {:class "gap-2 flex flex-row flex-wrap"}]
                         (map (fn [value]
                                (let [{:keys [text name]} (some #(when (= value (:value %)) %) options)]
                                  [ui.chip/chip {} (str (or text name value))])))
                         field-value)
            :error error)
     [:div
      (into [:<>]
            (map (fn [{:keys [text name value data-cy]}]
                   (let [value-set  (set field-value)
                         contained? (contains? value-set value)]
                     [ui.dropdown-menu/dropdown-menu-checkbox
                      {:icon Check
                       :checked contained?
                       :on-checked-change (fn []
                                            (let [new-values (if contained?
                                                               (disj value-set value)
                                                               (conj value-set value))]
                                              (on-change (vec new-values))))
                       :data-cy (when data-cy (str data-cy "-" value))
                       :class "outline-hidden text-sm hover:text-kleene-dark-05 transition-colors cursor-pointer text-ellipsis"}
                      (or text name)]))
                 options))]]))

(defn select [{:keys [field errors id label native required multiple helper value on-change
                      full-width size icon options class disabled data-cy placeholder field-props label-props
                      searchable variant]
               :or   {required   false
                      field      {}
                      full-width true
                      disabled   false
                      native     true
                      multiple   false
                      options    []}
               :as   params}]
  (let [error       (get errors id)
        field-value (or value
                        ;; Radix select accepts only strings
                        ;; if you try setting it to a non-string value, it will
                        ;; call the onChange event _again_ with a stringified
                        ;; value instead.
                        (str (:value field))
                        (if multiple [] ""))
        icon-size (if (= :large size) 24 20)
        on-change (or on-change
                      (fn [evt]
                        ((:action field) evt)
                        false))]

    (if multiple
      [multiselect
       (merge
        params
        field-props)]


      [:div {:class   (clsx {"min-w-[150px]" (not full-width)
                             "w-full"        full-width}
                            class)
             :data-cy (when data-cy (str data-cy "-wrapper"))}
       (when label
         [typography/label (merge {:for     id
                                   :htmlFor id}
                                  label-props)
          label (when required " *")])
       [:div
        [:div {:class "relative"}
         (when icon
           [:div {:class "absolute top-1/2 left-3 -translate-y-1/2 pointer-events-none"}
            [:> icon {:size  icon-size
                      :class "block text-kleene-dark-05"}]])

         ;; TODO: on-change event passes evt obj for native and value for radix.
         ;; Need to unify this.
         (cond
           native
           [select-native (merge {:options   options
                                  :value     field-value
                                  :error     error
                                  :icon      icon
                                  :disabled  disabled
                                  :placeholder placeholder
                                  :size      size
                                  :required  required
                                  :on-change on-change}
                                 field-props)]

           :else
           [select-radix (merge {:options     options
                                 :value       field-value
                                 :error       error
                                 :icon        icon
                                 :disabled    disabled
                                 :placeholder placeholder
                                 :size        size
                                 :on-change   on-change
                                 :searchable  searchable
                                 :required    required
                                 :variant     variant
                                 :data-cy     data-cy}
                                field-props)])]
        (when (or error helper)
          [:div {:class "mt-1 text-xs leading-5"}
           (cond
             error [:span {:class "text-kleene-error-01"} (or (:value error)
                                                              error)]
             helper [:span {:class "text-kleene-dark-05"} helper])])]])))


(defn checkbox [{:keys [field id label required disabled class classes on-change on-click checked full-width]
                 :or   {required false
                        disabled false}}]
  (let [value     (if field (:value field) checked)
        on-change (if field (:action field) on-change)
        checked   (if (#{"off" "" "false" "nil"} value) false (boolean value))]
    [:div {:class (clsx "flex gap-2"
                        {"w-full" full-width}
                        (:root classes))}
     [:> RadixCheckbox/Root {:id id
                             :class (clsx "flex justify-center items-center h-5 w-5 rounded-xs outline-hidden transition-colors border border-kleene-neutral-01 focus:ring-2 focus:ring-kleene-secondary-05"
                                          "dark:border-kleene-dark-05 dark:focus:ring-kleene-secondary-05/30"
                                          {"bg-kleene-secondary-02 border-kleene-secondary-02"  (and checked (not disabled))
                                           "bg-kleene-secondary-05 dark:bg-kleene-dark-02" disabled}
                                          class)
                             :checked checked
                             :on-checked-change (when-not disabled on-change)
                             :on-click          on-click
                             :required required
                             :disabled disabled}
      [:> RadixCheckbox/Indicator {:class "text-white dark:radix-state-checked:data-disabled:opacity-50"}
       (if (= value "indeterminate")
         [:> Minus {:size 18 :class "block" :strokeWidth 3}]
         [:> Check {:size 18 :class "block" :strokeWidth 3}])]]
     (when label
       [:label {:htmlFor id
                :class (clsx ""
                             {"cursor-pointer" (not disabled)
                              "text-kleene-dark-03 dark:text-kleene-neutral-01/50" disabled})}
        label])]))

(defn radio-root
  [& args]
  (let [[props children] (separate-props args)
        {:keys [field value id disabled required class on-value-change]} props]
    (into [:> RadixRadio/Root {:name            id
                               :id              id
                               :value           (or value (:value field))
                               :on-value-change (or on-value-change (:action field))
                               :required        required
                               :disabled        disabled
                               :class           class}]
          children)))

(defn radio-inidicator []
  [:> RadixRadio/Indicator {:class (clsx "block rounded-full h-2.5 w-2.5 bg-kleene-secondary-02 data-disabled:bg-kleene-secondary-04 dark:data-disabled:opacity-50")}])

(defn radio-item [{:keys [id value disabled required]}]
  [:> RadixRadio/Item {:value    value
                       :required required
                       :disabled disabled
                       :id       (str id "-" value)
                       :class    (clsx "flex justify-center items-center h-5 w-5 rounded-full border border-kleene-neutral-01 focus:ring-2 focus:ring-kleene-secondary-05 text-kleene-secondary-02 transition-colors"
                                       "dark:border-kleene-dark-05 dark:focus:ring-kleene-secondary-05/30"
                                       {"bg-kleene-secondary-05 dark:bg-transparent" disabled})}
   [radio-inidicator]])

(defn radio-group
  [{:keys [id label options disabled required] :as props}]
  [:div {:class "flex flex-col gap-2"}
   (when label
     [typography/label {:for id}
      label (when required " *")])
   [radio-root (-> props
                   (update :class #(clsx "flex flex-col gap-2" %)))
    (for [{:keys [value text]} options]
      ^{:key value}
      [:div {:class "flex gap-2"}
       [radio-item {:id       id
                    :value    value
                    :required required
                    :disabled disabled}]
       [:label {:htmlFor (str id "-" value)
                :class   (clsx ""
                               {"cursor-pointer" (not disabled)
                                "text-kleene-dark-03 dark:text-kleene-neutral-01/50" disabled})}
        text]])]])

(defn switch
  [{:keys [id field classes on-change] :as props}]
  (let [{:keys [value action]} field]
    [:div {:class (clsx "flex gap-2 items-center" (:root classes))}
     [:> RadixSwitch/Root
      (cond-> props
        true      (dissoc :field :label :on-change :classes)
        field     (assoc :checked value :on-checked-change action)
        on-change (assoc :on-checked-change on-change)
        true      (update :class #(clsx "peer inline-flex p-0.5 w-[38px] shrink-0 cursor-pointer items-center rounded-full border border-kleene-input-border transition-colors focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 radix-state-checked:bg-kleene-secondary-v2 radix-state-unchecked:bg-kleene-background" %)))
      [:> RadixSwitch/Thumb {:class "pointer-events-none block h-4 w-4 rounded-full bg-kleene-input-border shadow-lg ring-0 transition-transform radix-state-checked:translate-x-4 radix-state-checked:bg-white radix-state-unchecked:translate-x-0"}]]
     (when (:label props)
       [label {:for id}
        (:label props)])]))
